import { redirect } from "react-router";
import { bootstrapAuth } from "@/auth/me";
import { loginUrlFor } from "@/auth/login-url";
import { fetchPermissionCodes, hasAnySystemPermissionCode, Permission } from "@/auth/access-permissions";
import { useAuthStore } from "@/stores/auth-store";

// React Router data router loader. Used on every protected route.
// Cheap: if the auth store already says we're authenticated, returns
// immediately. Only hits /auth/me when status is still "loading"
// (first navigation) or after an explicit invalidation.
//
// On anonymous: throws a redirect to /auth/login with the current
// URL as `next=` so post-login navigation lands where the user tried
// to go.

export async function requireAuth({ request }: { request: Request }): Promise<null> {
  const state = useAuthStore.getState();
  if (state.status === "loading") {
    await bootstrapAuth();
  }
  const after = useAuthStore.getState();
  if (after.status !== "authenticated") {
    const url = new URL(request.url);
    throw redirect(loginUrlFor(url.pathname + url.search));
  }
  return null;
}

// Route loader for the Systemeinstellungen surface. Runs requireAuth first
// (redirects anonymous users to login), then redirects authenticated users
// without any system-settings Permission to the app root. genesis still
// enforces every write server-side (`principal_grants`, not this check);
// this just keeps the pages out of reach. 242-access-model: reads
// `myPermissions` rather than a JWT role, since genesis no longer emits one.
export async function requireSystemAccess(args: { request: Request }): Promise<null> {
  await requireAuth(args);
  const codes = await fetchPermissionCodes();
  if (!hasAnySystemPermissionCode(codes)) {
    throw redirect("/");
  }
  return null;
}

// Route loader for /c/new. Level A of the two-tier company model
// (the company account model): the create:company Permission means "may
// create Companies at all" and nothing more. It must never be used to reach an existing
// Company's admin surface — that is a company_membership row (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization) and is checked
// on /c/:id, not here. Presentation only; genesis re-reads the Permission on createCompany.
export async function requireCompanyCreation(args: { request: Request }): Promise<null> {
  await requireAuth(args);
  const codes = await fetchPermissionCodes();
  if (!codes.includes(Permission.CreateCompany)) {
    throw redirect("/");
  }
  return null;
}
